import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import { ReceiptLongRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  useRecordTestInvoicePayment,
  useRunTestPostpaidSweep,
  useTestPostpaidStatus,
} from "@/hooks/useCampaignTester";
import type { PostpaidSweepResult } from "@/interfaces/campaignTester";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { formatDate } from "@/utils/date-format";
import { formatUsd } from "@/utils/number-format";

/** RequiredPaymentPurpose / Mode / Status — mirror the API constants (constants/campaignCredits.ts). */
const PURPOSE: Record<number, string> = { 0: "Deposit", 1: "Final invoice", 2: "Supplementary" };
const MODE: Record<number, string> = { 0: "Card", 1: "Invoice", 2: "Credits" };
const STATUS: Record<
  number,
  { label: string; colour: "default" | "info" | "success" | "warning" }
> = {
  0: { label: "Unissued", colour: "default" },
  1: { label: "Issued", colour: "info" },
  2: { label: "Paid", colour: "success" },
  3: { label: "Void", colour: "warning" },
};
/** InvoiceStatus values an invoice can take a payment from: pending, partial, overdue. */
const PAYABLE_INVOICE = new Set([1, 2, 5]);

const th = {
  fontSize: 10.5,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 700,
  whiteSpace: "nowrap" as const,
};

function Level({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Box sx={{ minWidth: 132 }}>
      <Typography sx={th}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>{hint}</Typography>
    </Box>
  );
}

/**
 * A postpaid campaign: the split it launched with, the ceiling as redemptions draw it, and the
 * payments it requires — the deposit, and the final invoice raised after close.
 *
 * Redemptions take the deposit first and the ceiling after, so the two meters are read together:
 * the ceiling does not move until the deposit is spent. The sweep button is the daily cron scoped to
 * this campaign; it raises a real final invoice (Stripe test mode) once the campaign has closed.
 *
 * Renders nothing for any other funding model.
 */
export interface PostpaidPanelProps {
  campaignId: number;
  /** The tester's simulated date — the sweep raises what is due on it. */
  simulatedDate: string;
}

export default function PostpaidPanel({ campaignId, simulatedDate }: PostpaidPanelProps) {
  const { data: status, isLoading } = useTestPostpaidStatus(campaignId);
  const sweep = useRunTestPostpaidSweep(campaignId);
  const pay = useRecordTestInvoicePayment(campaignId);
  const [lastRun, setLastRun] = useState<PostpaidSweepResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <CircularProgress aria-label="Loading" size={20} />
      </Box>
    );
  }
  if (!status) return null;

  const money = (v: string | null) => (v == null ? "—" : `${Number(v).toLocaleString()} cr`);
  const usd = (v: string | null) => formatUsd(v);
  const deposit = Number(status.deposit);
  const depositUsed = deposit - Number(status.depositRemaining);
  const ceilingOriginal = Number(status.ceiling?.original ?? 0);
  const ceilingUsed = Number(status.draws.credits);

  async function runSweep() {
    setError(null);
    try {
      setLastRun(await sweep.mutateAsync(`${simulatedDate}T00:00:00.000Z`));
    } catch (e) {
      setError(apiErrorMessage(e, "The sweep was refused."));
    }
  }

  async function recordPayment(invoiceId: number) {
    setError(null);
    try {
      await pay.mutateAsync(invoiceId);
    } catch (e) {
      setError(apiErrorMessage(e, "The payment was refused."));
    }
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {FUNDING_MODEL_NAME.postpaid}
        </Typography>
        <Chip size="small" label={`${Number(status.minUpfrontPc)}% upfront`} />
        {status.campaignClosed && <Chip size="small" label="Campaign closed" />}
        {status.poNumber && (
          <Chip size="small" variant="outlined" label={`PO ${status.poNumber}`} />
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        <Level label="Budget" value={money(status.approvedBudget)} hint="approved at launch" />
        <Level
          label="Deposit"
          value={money(status.deposit)}
          hint={`${Number(status.minUpfrontPc)}% of the budget, whole gifts`}
        />
        <Level
          label="Credits applied"
          value={money(status.creditsApplied)}
          hint="from the wallet, toward the deposit"
        />
        <Level label="Due today" value={money(status.dueToday)} hint="card or deposit invoice" />
        <Level
          label="Billable ceiling"
          value={money(status.ceiling?.original ?? null)}
          hint="invoiced after close, up to"
        />
        <Level
          label="Credit limit"
          value={status.creditLimit == null ? "—" : money(status.creditLimit)}
          hint={`org exposure now ${money(status.exposure)}`}
        />
      </Stack>

      <Stack direction="row" gap={3} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={th}>Deposit used</Typography>
          <LinearProgress
            variant="determinate"
            value={deposit > 0 ? (depositUsed / deposit) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, my: 0.75 }}
          />
          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
            {money(String(depositUsed))} of {money(status.deposit)} — redemptions draw this first
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={th}>Ceiling drawn</Typography>
          <LinearProgress
            variant="determinate"
            color="warning"
            value={ceilingOriginal > 0 ? (ceilingUsed / ceilingOriginal) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, my: 0.75 }}
          />
          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
            {money(String(ceilingUsed))} over {status.draws.count} gift
            {status.draws.count === 1 ? "" : "s"} ({usd(status.draws.usd)}) ·{" "}
            {money(status.ceiling?.remaining ?? null)} left
            {status.campaignClosed ? " (released at close)" : ""}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        {/* The daily cron, for this campaign alone, on the simulated date. A final payment is only
            picked up once the campaign has closed — expire it first. */}
        <Button
          variant="contained"
          startIcon={<ReceiptLongRounded />}
          disabled={sweep.isPending}
          onClick={() => void runSweep()}
          sx={{ bgcolor: "var(--color-dark-dust)" }}
        >
          Run invoice sweep
        </Button>
        {sweep.isPending && <CircularProgress aria-label="Loading" size={18} />}
        {lastRun && !sweep.isPending && (
          <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
            {lastRun.issued.length} issued · {lastRun.voided.length} voided ·{" "}
            {lastRun.failed.length} failed
            {lastRun.issued.length + lastRun.voided.length + lastRun.failed.length === 0
              ? status.campaignClosed
                ? " — nothing due on this date"
                : " — the final invoice waits for the campaign to close"
              : ""}
          </Typography>
        )}
      </Stack>
      {lastRun?.failed.map((f) => (
        <Alert key={f.requiredPaymentId} severity="error" sx={{ mb: 1 }}>
          Required payment {f.requiredPaymentId}: {f.error}
        </Alert>
      ))}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Required payments</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={th}>#</TableCell>
            <TableCell sx={th}>Purpose</TableCell>
            <TableCell sx={th}>Mode</TableCell>
            <TableCell sx={th}>Status</TableCell>
            <TableCell sx={th} align="right">
              Amount
            </TableCell>
            <TableCell sx={th}>Issue</TableCell>
            <TableCell sx={th}>Due</TableCell>
            <TableCell sx={th}>Invoice</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {status.requiredPayments.map((p) => {
            const s = STATUS[p.status] ?? { label: String(p.status), colour: "default" };
            const payable = p.invoice != null && PAYABLE_INVOICE.has(p.invoice.status);
            return (
              <TableRow key={p.id}>
                <TableCell>{p.id}</TableCell>
                <TableCell>{PURPOSE[p.purpose] ?? p.purpose}</TableCell>
                <TableCell>{MODE[p.mode] ?? p.mode}</TableCell>
                <TableCell>
                  <Chip size="small" color={s.colour} label={s.label} />
                </TableCell>
                {/* A deposit row carries credits; a final row carries USD, priced at issue. */}
                <TableCell align="right">
                  {p.purpose === 1 ? usd(p.amount) : money(p.amount)}
                </TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{formatDate(p.issueDate)}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>
                  {p.dueDate ? formatDate(p.dueDate) : "—"}
                </TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>
                  {p.invoice == null ? (
                    "—"
                  ) : p.invoice.hostedInvoiceUrl ? (
                    <Link href={p.invoice.hostedInvoiceUrl} target="_blank" rel="noopener">
                      {p.invoice.invoiceNumber ?? `#${p.invoice.id}`}
                    </Link>
                  ) : (
                    (p.invoice.invoiceNumber ?? `#${p.invoice.id}`)
                  )}
                </TableCell>
                <TableCell align="right">
                  {payable && p.invoice && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={pay.isPending}
                      onClick={() => void recordPayment(p.invoice?.id as number)}
                    >
                      Record payment
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)", mt: 1 }}>
        "Record payment" is Finance's payment-received route. Paying on the invoice's Stripe page
        with a test card goes through the webhook instead — both mark the required payment paid.
      </Typography>
    </Box>
  );
}
